/**
 * This file includes the sagas that are related to reporting errors to the
 * server, Prometheus monitoring and displaying the "Sparx Web error" alert.
 */
import { put, call, takeEvery, select } from 'redux-saga/effects';

import { showAlert } from '../alert/sagas';

import { getSessionType } from 'phoenix2/state/session/selectors';
import { SOLO } from 'phoenix2/state/session/session-types';

import { parseException } from 'phoenix2/utils/exceptions';

import { REPORT_ERROR, SPARX_WEB_ERROR } from './actions';

import { stopExplicitWaiting, stopImplicitWaiting } from '../wait/actions';
import { getBrowserDetailsLabel } from '../browser-details/selectors';
import { CLIENT_ERROR_LEVEL_ERROR } from './errors';

import * as Sentry from '@sentry/react';

export function* watchReportError(session, analytics = null) {
    yield takeEvery(
        REPORT_ERROR,
        function*(session, analytics, action) {
            yield reportError(session, analytics, action.error, action.frames, action.level);
        },
        session,
        analytics,
    );
}

/**
 * Reports a client error to the server. This is called by the watchReportError takeEvery, but
 * also directly by the handleFatalError saga in user/sagas.js
 * @param session   - the session (used to make the request)
 * @param analytics - the analytics object (used for Prometheus monitoring)
 * @param error     - a string, error or Immutable error
 * @param frames    - a string representing the stack trace
 *                      (if supplied overrides stack trace in error)
 * @param level     - the logging level for the error to be reported (see ./errors.js)
 */
export function* reportError(
    session,
    analytics,
    error,
    frames = null,
    level = CLIENT_ERROR_LEVEL_ERROR,
) {
    const parsed = yield parseException(error);
    if (!parsed.reportToServer) {
        return;
    }

    // Temporary hack for 2.75: silence errors about Guardware plugin (ALG-18505)
    if (
        parsed.message &&
        parsed.message.indexOf &&
        parsed.message.indexOf("Cannot read property 'background' of undefined") !== -1
    ) {
        return;
    }

    const browserDetails = yield select(getBrowserDetailsLabel) || '?';

    const errorMessage =
        'category:' +
        parsed.category +
        ' - ' +
        parsed.name +
        ': ' +
        parsed.message +
        ' - browser: ' +
        browserDetails;

    // Report the error to Prometheus (only if it is an error not just a warning)
    if (analytics && level === CLIENT_ERROR_LEVEL_ERROR) {
        analytics.exception(errorMessage);
        analytics.flush();
    }

    // Report the error to Sentry
    Sentry.captureException(error);
}

export function* watchsparxWebError() {
    yield takeEvery(SPARX_WEB_ERROR, sparxWebError);
}

/**
 * Shows the "Sparx Web error" alert and reloads the page.
 */
export function* sparxWebError() {
    yield put(stopExplicitWaiting());
    yield put(stopImplicitWaiting());
    const sessionType = yield select(getSessionType);

    yield call(showAlert, {
        title: 'Oops!',
        message: `${
            sessionType === SOLO ? 'Numerise' : 'Sparx Maths'
        } has experienced an error. Please refresh the page.`,
    });

    // Reload the app to take the user back to the login screen (can't do a
    // logout action in case the root saga is broken):
    location.reload();
}
